import type { Lang } from "./index";

// Legal-document copy (privacy policy + terms of service) in all three
// shipped languages. Each language is a faithful, complete translation:
// no clause is summarised or dropped. The Polish entries are the verbatim
// original text. NOTE: these are machine translations of legal text — a
// lawyer should review the EN/DE versions before they are relied upon.

// --- Privacy policy ----------------------------------------------------

export type PrivacyCopy = {
  topBarBack: string;
  topBarTitle: string;
  h1: string;
  metaUpdated: string;

  s1Heading: string;
  s1Body: string;

  s2Heading: string;
  // List items carry inline <strong>...</strong> emphasis on the lead-in.
  s2ItemIdentifyingLead: string;
  s2ItemIdentifyingRest: string;
  s2ItemHealthLead: string;
  s2ItemHealthRest: string;
  s2ItemFoodLead: string;
  s2ItemFoodRest: string;
  s2ItemOpsLead: string;
  s2ItemOpsRest: string;
  s2ItemSessionLead: string;
  // Wraps the <code>sf_session</code> token, hence two halves.
  s2ItemSessionBefore: string;
  s2ItemSessionAfter: string;

  s3Heading: string;
  s3ItemContractLead: string;
  s3ItemContractRest: string;
  s3ItemConsentLead: string;
  s3ItemConsentRest: string;
  s3ItemInterestLead: string;
  s3ItemInterestRest: string;

  s4Heading: string;
  s4ItemCloudflareLead: string;
  s4ItemCloudflareRest: string;
  // Anthropic item is split around an embedded <strong> phrase.
  s4ItemAnthropicLead: string;
  s4ItemAnthropicBefore: string;
  s4ItemAnthropicEmph: string;
  s4ItemAnthropicAfter: string;
  s4NoSellLead: string;
  s4NoSellRest: string;

  s5Heading: string;
  s5ItemAccount: string;
  s5ItemAudit: string;
  s5ItemRateLimit: string;
  s5ItemPhotos: string;

  s6Heading: string;
  s6ItemAccessLead: string;
  s6ItemAccessRest: string;
  s6ItemRectifyLead: string;
  s6ItemRectifyRest: string;
  s6ItemEraseLead: string;
  s6ItemEraseRest: string;
  s6ItemObjectLead: string;
  s6ItemObjectRest: string;
  s6ItemComplaintLead: string;
  s6ItemComplaintRest: string;

  s7Heading: string;
  s7Before: string;
  s7Emph: string;
  s7Middle: string;
  s7Phone: string;
  s7After: string;

  s8Heading: string;
  s8Before: string;
  s8After: string;

  s9Heading: string;
  s9Body: string;
};

// --- Terms of service --------------------------------------------------

export type TermsCopy = {
  topBarBack: string;
  topBarTitle: string;
  h1: string;
  metaUpdated: string;

  s1Heading: string;
  s1Body: string;

  s2Heading: string;
  s2ItemAge: string;
  s2ItemAccount: string;
  s2ItemHousehold: string;
  s2ItemDelete: string;

  s3Heading: string;
  s3Before: string;
  s3Emph1: string;
  s3Middle: string;
  s3Emph2: string;
  s3After: string;

  s4Heading: string;
  s4IntroEmph: string;
  s4IntroRest: string;
  s4ItemAdultsLead: string;
  s4ItemAdultsPhone: string;
  s4ItemChildrenLead: string;
  s4ItemChildrenPhone: string;
  s4ItemEmergencyLead: string;
  s4ItemEmergencyPhone: string;

  s5Heading: string;
  s5ItemReverse: string;
  s5ItemUpload: string;
  s5ItemPromote: string;
  s5ItemSpam: string;

  s6Heading: string;
  s6Body1: string;
  s6Body2: string;

  s7Heading: string;
  s7Body: string;

  s8Heading: string;
  s8Body: string;

  s9Heading: string;
  s9Before: string;
  s9LinkLabel: string;
  s9After: string;
};

export type LegalCopy = {
  privacy: PrivacyCopy;
  terms: TermsCopy;
};

// --- Polish (verbatim original) ----------------------------------------

const pl: LegalCopy = {
  privacy: {
    topBarBack: "Wróć",
    topBarTitle: "POLITYKA",
    h1: "Polityka",
    metaUpdated: "AKTUALIZACJA · 14 MAJA 2026",

    s1Heading: "1. Administrator danych",
    s1Body:
      "Administratorem Twoich danych osobowych w aplikacji Sytofood jest właściciel projektu. Kontakt: hello@sytofood.com.",

    s2Heading: "2. Jakie dane przetwarzamy",
    s2ItemIdentifyingLead: "Identyfikujące:",
    s2ItemIdentifyingRest:
      " email, imię (opcjonalnie), hash hasła (PBKDF2 100 000 iteracji — nigdy nie trzymamy hasła w czystej formie).",
    s2ItemHealthLead: "Profil zdrowotny",
    s2ItemHealthRest:
      " (art. 9 RODO — szczególna kategoria): waga, wzrost, rok urodzenia, płeć biologiczna, poziom aktywności, cel kaloryczny i makroskładniki. Wszystkie te pola są opcjonalne.",
    s2ItemFoodLead: "Dane o jedzeniu:",
    s2ItemFoodRest:
      " logowane posiłki (nazwa, kcal, makro, data), ulubione, zawartość lodówki, lista zakupów.",
    s2ItemOpsLead: "Operacyjne:",
    s2ItemOpsRest:
      " logi (akcje typu login, logowanie posiłku), adres IP w kluczach rate-limit (przy logowaniu i rejestracji).",
    s2ItemSessionLead: "Sesja:",
    s2ItemSessionBefore: " cookie ",
    s2ItemSessionAfter:
      " (HttpOnly, Secure, SameSite=Lax) — cookie funkcjonalne, niezbędne do działania.",

    s3Heading: "3. Podstawy prawne",
    s3ItemContractLead: "Wykonanie umowy",
    s3ItemContractRest:
      " (art. 6 ust. 1 lit. b RODO) — dla core'owego liczenia kalorii i obsługi konta.",
    s3ItemConsentLead: "Wyraźna zgoda",
    s3ItemConsentRest:
      " (art. 9 ust. 2 lit. a RODO) — dla danych o zdrowiu (waga, wzrost, sex, kcal, foto posiłków przez AI). Możesz wycofać w każdej chwili usuwając konto.",
    s3ItemInterestLead: "Uzasadniony interes",
    s3ItemInterestRest:
      " (art. 6 ust. 1 lit. f RODO) — bezpieczeństwo (rate-limit IP), audyt operacji w koncie.",

    s4Heading: "4. Komu przekazujemy dane",
    s4ItemCloudflareLead: "Cloudflare, Inc.",
    s4ItemCloudflareRest:
      " (USA, EU regions): host aplikacji, baza D1, AI Gateway, Workers AI, Email Sending. DPA + SCC.",
    s4ItemAnthropicLead: "Anthropic, PBC",
    s4ItemAnthropicBefore:
      " (USA): zdjęcia menu / lodówki / posiłków są wysyłane do Claude Sonnet 4.6 przez AI Gateway na potrzeby rozpoznawania zawartości. Zdjęcia ",
    s4ItemAnthropicEmph: "nie są persistowane",
    s4ItemAnthropicAfter: " u nas ani u Anthropic. DPA + SCC.",
    s4NoSellLead: "Nie sprzedajemy",
    s4NoSellRest:
      " Twoich danych. Nie używamy analytics third-party (Google, Meta itd.).",

    s5Heading: "5. Jak długo trzymamy",
    s5ItemAccount:
      "Dane konta i posiłki — dopóki utrzymujesz konto. Po usunięciu konta znikają natychmiast (cascade w bazie).",
    s5ItemAudit: "Audyt operacji — 12 miesięcy, potem auto-prune.",
    s5ItemRateLimit: "Rate-limit (IP) — 7 dni, potem auto-prune.",
    s5ItemPhotos:
      "Zdjęcia (menu, lodówka, posiłek) — nigdy nie trzymane. Wysyłka do AI → odpowiedź → koniec.",

    s6Heading: "6. Twoje prawa",
    s6ItemAccessLead: "Dostęp + przenoszalność",
    s6ItemAccessRest:
      " (art. 15, 20) — pobierz JSON ze wszystkimi swoimi danymi w Dashboardzie.",
    s6ItemRectifyLead: "Sprostowanie",
    s6ItemRectifyRest:
      " (art. 16) — edytuj profil, posiłki, lodówkę w aplikacji.",
    s6ItemEraseLead: "Usunięcie",
    s6ItemEraseRest:
      ' (art. 17, "right to be forgotten") — przycisk "Usuń konto" w Dashboardzie. Hard-delete, bez odzyskiwania.',
    s6ItemObjectLead: "Sprzeciw",
    s6ItemObjectRest:
      " (art. 21) — jeśli nie zgadzasz się na AI processing, nie używaj funkcji foto. Bez foto aplikacja nadal działa.",
    s6ItemComplaintLead: "Skarga",
    s6ItemComplaintRest:
      " — do organu nadzorczego (w PL: Prezes UODO, uodo.gov.pl).",

    s7Heading: "7. Disclaimer medyczny",
    s7Before: "Sytofood ",
    s7Emph: "nie zastępuje porady dietetyka ani lekarza",
    s7Middle:
      ". Wartości kaloryczne i makroskładnikowe są szacunkami AI z marginesem błędu. Przy zaburzeniach odżywiania prosimy o kontakt z Telefonem Zaufania: ",
    s7Phone: "116 123",
    s7After: ".",

    s8Heading: "8. Cookies",
    s8Before: "Używamy jednego cookie funkcjonalnego (",
    s8After:
      ", HttpOnly). Jest niezbędny do logowania — bez niego nie można korzystać z konta. Pod ePrivacy nie wymaga zgody. Nie używamy cookies trackerowych.",

    s9Heading: "9. Kontakt",
    s9Body:
      "Pytania o prywatność, zgłoszenia, prośby o dane: hello@sytofood.com. Odpowiadamy w 30 dni (art. 12 ust. 3 RODO).",
  },
  terms: {
    topBarBack: "Wróć",
    topBarTitle: "REGULAMIN",
    h1: "Regulamin",
    metaUpdated: "AKTUALIZACJA · 14 MAJA 2026",

    s1Heading: "1. Co to jest Sytofood",
    s1Body:
      "Sytofood to webaplikacja (PWA) do liczenia kalorii i planowania posiłków, z wirtualną lodówką i sugestiami AI. Korzystanie wymaga założenia konta.",

    s2Heading: "2. Konto",
    s2ItemAge: "Minimalny wiek: 16 lat (lub zgoda opiekuna).",
    s2ItemAccount: "Jedno konto na osobę. Hasło min. 8 znaków.",
    s2ItemHousehold:
      "Możesz dołączyć do household (rodzina) przez kod zaproszenia — wtedy dzielisz z innymi members listę produktów, lodówkę i zakupy. Twoje posiłki pozostają domyślnie prywatne.",
    s2ItemDelete: "Możesz usunąć konto w każdej chwili (Dashboard → Usuń konto).",

    s3Heading: "3. Co robimy z AI",
    s3Before:
      "Foto posiłków, lodówki i menu jest wysyłane do Claude Sonnet 4.6 (Anthropic, USA) przez Cloudflare AI Gateway. Zdjęcia ",
    s3Emph1: "nie są persistowane",
    s3Middle: ". Estymacje kcal/makro to ",
    s3Emph2: "szacunki AI z marginesem błędu",
    s3After: " — nie traktuj ich jako doradztwa medycznego/dietetycznego.",

    s4Heading: "4. Disclaimer medyczny",
    s4IntroEmph: "Sytofood nie jest urządzeniem medycznym.",
    s4IntroRest:
      " Aplikacja nie stawia diagnoz, nie zaleca leczenia, nie zastępuje wizyty u dietetyka ani lekarza. Przy zaburzeniach odżywiania kontakt:",
    s4ItemAdultsLead: "Telefon Zaufania dla Dorosłych: ",
    s4ItemAdultsPhone: "116 123",
    s4ItemChildrenLead: "Telefon Zaufania dla Dzieci i Młodzieży: ",
    s4ItemChildrenPhone: "116 111",
    s4ItemEmergencyLead: "Numer alarmowy: ",
    s4ItemEmergencyPhone: "112",

    s5Heading: "5. Co jest niedozwolone",
    s5ItemReverse: "Reverse-engineering, scrape API, omijanie rate limitów.",
    s5ItemUpload: "Wgrywanie cudzych zdjęć / cudzych danych zdrowotnych.",
    s5ItemPromote:
      "Wykorzystywanie aplikacji do promowania niezdrowych zachowań.",
    s5ItemSpam: "Spam / automatyczne konta.",

    s6Heading: "6. Cena i dostępność",
    s6Body1:
      "Aktualnie Sytofood jest darmowy w fazie testowej. Możemy w przyszłości wprowadzić plan płatny dla nowych funkcji — istniejące konta dostaną powiadomienie z minimum 30-dniowym wyprzedzeniem.",
    s6Body2:
      "Best-effort uptime, brak SLA. Możemy zawiesić konto za naruszenie regulaminu po wcześniejszym powiadomieniu.",

    s7Heading: "7. Odpowiedzialność",
    s7Body:
      "Korzystasz z Sytofood na własną odpowiedzialność. Nie ponosimy odpowiedzialności za skutki diet ustalonych na podstawie szacunków AI — zawsze konsultuj się z dietetykiem przy diecie eliminacyjnej, ciąży, chorobie przewlekłej.",

    s8Heading: "8. Zmiany regulaminu",
    s8Body:
      "Powiadomimy mailem 14 dni przed wejściem zmian. Brak akceptacji → możliwość usunięcia konta z pełnym eksportem danych.",

    s9Heading: "9. Prawo i sąd",
    s9Before:
      "Prawo polskie. Sąd właściwy miejscowo dla siedziby administratora. Spory konsumenckie — możliwość ODR via ",
    s9LinkLabel: "ec.europa.eu/consumers/odr",
    s9After: ".",
  },
};

// --- English -----------------------------------------------------------

const en: LegalCopy = {
  privacy: {
    topBarBack: "Back",
    topBarTitle: "POLICY",
    h1: "Policy",
    metaUpdated: "UPDATED · 14 MAY 2026",

    s1Heading: "1. Data controller",
    s1Body:
      "The controller of your personal data within the Sytofood application is the project owner. Contact: hello@sytofood.com.",

    s2Heading: "2. What data we process",
    s2ItemIdentifyingLead: "Identifying:",
    s2ItemIdentifyingRest:
      " email, first name (optional), password hash (PBKDF2 with 100,000 iterations, we never store the password in plain form).",
    s2ItemHealthLead: "Health profile",
    s2ItemHealthRest:
      " (Art. 9 GDPR (RODO), special category): weight, height, year of birth, biological sex, activity level, calorie goal and macronutrients. All of these fields are optional.",
    s2ItemFoodLead: "Food data:",
    s2ItemFoodRest:
      " logged meals (name, kcal, macros, date), favourites, fridge contents, shopping list.",
    s2ItemOpsLead: "Operational:",
    s2ItemOpsRest:
      " logs (actions such as login, meal logging), IP address in rate-limit keys (during sign-in and registration).",
    s2ItemSessionLead: "Session:",
    s2ItemSessionBefore: " the ",
    s2ItemSessionAfter:
      " cookie (HttpOnly, Secure, SameSite=Lax), a functional cookie that is necessary for the service to work.",

    s3Heading: "3. Legal bases",
    s3ItemContractLead: "Performance of a contract",
    s3ItemContractRest:
      " (Art. 6(1)(b) GDPR (RODO)), for the core calorie counting and account handling.",
    s3ItemConsentLead: "Explicit consent",
    s3ItemConsentRest:
      " (Art. 9(2)(a) GDPR (RODO)), for health data (weight, height, sex, kcal, meal photos processed by AI). You can withdraw it at any time by deleting your account.",
    s3ItemInterestLead: "Legitimate interest",
    s3ItemInterestRest:
      " (Art. 6(1)(f) GDPR (RODO)), security (IP rate-limiting) and auditing of account operations.",

    s4Heading: "4. Who we share data with",
    s4ItemCloudflareLead: "Cloudflare, Inc.",
    s4ItemCloudflareRest:
      " (USA, EU regions): application hosting, D1 database, AI Gateway, Workers AI, Email Sending. DPA + SCC.",
    s4ItemAnthropicLead: "Anthropic, PBC",
    s4ItemAnthropicBefore:
      " (USA): photos of menus, fridge and meals are sent to Claude Sonnet 4.6 through the AI Gateway for the purpose of recognising their contents. Photos ",
    s4ItemAnthropicEmph: "are not persisted",
    s4ItemAnthropicAfter: " by us or by Anthropic. DPA + SCC.",
    s4NoSellLead: "We do not sell",
    s4NoSellRest:
      " your data. We do not use third-party analytics (Google, Meta, etc.).",

    s5Heading: "5. How long we keep data",
    s5ItemAccount:
      "Account data and meals, for as long as you keep your account. After the account is deleted they are removed immediately (database cascade).",
    s5ItemAudit:
      "Operation audit, 12 months, then automatically pruned.",
    s5ItemRateLimit: "Rate-limit (IP), 7 days, then automatically pruned.",
    s5ItemPhotos:
      "Photos (menu, fridge, meal), never stored. Sent to the AI, response received, done.",

    s6Heading: "6. Your rights",
    s6ItemAccessLead: "Access and portability",
    s6ItemAccessRest:
      " (Art. 15, 20), download a JSON file with all of your data from the Dashboard.",
    s6ItemRectifyLead: "Rectification",
    s6ItemRectifyRest:
      " (Art. 16), edit your profile, meals and fridge inside the application.",
    s6ItemEraseLead: "Erasure",
    s6ItemEraseRest:
      ' (Art. 17, the "right to be forgotten"), the "Delete account" button in the Dashboard. Hard delete, with no recovery.',
    s6ItemObjectLead: "Objection",
    s6ItemObjectRest:
      " (Art. 21), if you do not agree to AI processing, do not use the photo features. The application still works without photos.",
    s6ItemComplaintLead: "Complaint",
    s6ItemComplaintRest:
      ", to a supervisory authority (in Poland: the President of the UODO, uodo.gov.pl).",

    s7Heading: "7. Medical disclaimer",
    s7Before: "Sytofood ",
    s7Emph: "does not replace advice from a dietitian or a doctor",
    s7Middle:
      ". Calorie and macronutrient values are AI estimates with a margin of error. If you are affected by an eating disorder, please contact the helpline: ",
    s7Phone: "116 123",
    s7After: ".",

    s8Heading: "8. Cookies",
    s8Before: "We use a single functional cookie (",
    s8After:
      ", HttpOnly). It is necessary for signing in, without it you cannot use your account. Under ePrivacy it does not require consent. We do not use tracking cookies.",

    s9Heading: "9. Contact",
    s9Body:
      "Questions about privacy, reports, data requests: hello@sytofood.com. We respond within 30 days (Art. 12(3) GDPR (RODO)).",
  },
  terms: {
    topBarBack: "Back",
    topBarTitle: "TERMS",
    h1: "Terms",
    metaUpdated: "UPDATED · 14 MAY 2026",

    s1Heading: "1. What Sytofood is",
    s1Body:
      "Sytofood is a web application (PWA) for counting calories and planning meals, with a virtual fridge and AI suggestions. Using it requires creating an account.",

    s2Heading: "2. Account",
    s2ItemAge:
      "Minimum age: 16 years (or with a guardian's consent).",
    s2ItemAccount:
      "One account per person. Password of at least 8 characters.",
    s2ItemHousehold:
      "You can join a household (family) via an invite code, in which case you share a product list, fridge and shopping list with the other members. Your meals stay private by default.",
    s2ItemDelete:
      "You can delete your account at any time (Dashboard → Delete account).",

    s3Heading: "3. What we do with AI",
    s3Before:
      "Photos of meals, fridge and menus are sent to Claude Sonnet 4.6 (Anthropic, USA) through the Cloudflare AI Gateway. Photos ",
    s3Emph1: "are not persisted",
    s3Middle: ". The kcal/macro estimates are ",
    s3Emph2: "AI estimates with a margin of error",
    s3After:
      ", do not treat them as medical or dietary advice.",

    s4Heading: "4. Medical disclaimer",
    s4IntroEmph: "Sytofood is not a medical device.",
    s4IntroRest:
      " The application does not make diagnoses, does not recommend treatment, and does not replace a visit to a dietitian or doctor. If you are affected by an eating disorder, contact:",
    s4ItemAdultsLead: "Helpline for Adults: ",
    s4ItemAdultsPhone: "116 123",
    s4ItemChildrenLead: "Helpline for Children and Young People: ",
    s4ItemChildrenPhone: "116 111",
    s4ItemEmergencyLead: "Emergency number: ",
    s4ItemEmergencyPhone: "112",

    s5Heading: "5. What is not allowed",
    s5ItemReverse:
      "Reverse engineering, scraping the API, circumventing rate limits.",
    s5ItemUpload:
      "Uploading other people's photos or other people's health data.",
    s5ItemPromote:
      "Using the application to promote unhealthy behaviour.",
    s5ItemSpam: "Spam or automated accounts.",

    s6Heading: "6. Price and availability",
    s6Body1:
      "Sytofood is currently free during the testing phase. We may introduce a paid plan for new features in the future, existing accounts will receive notice at least 30 days in advance.",
    s6Body2:
      "Best-effort uptime, no SLA. We may suspend an account for a breach of the terms after prior notice.",

    s7Heading: "7. Liability",
    s7Body:
      "You use Sytofood at your own risk. We are not liable for the consequences of diets set on the basis of AI estimates, always consult a dietitian in the case of an elimination diet, pregnancy, or a chronic illness.",

    s8Heading: "8. Changes to the terms",
    s8Body:
      "We will give notice by email 14 days before changes take effect. If you do not accept them, you may delete your account with a full export of your data.",

    s9Heading: "9. Law and jurisdiction",
    s9Before:
      "Polish law. The court with local jurisdiction is the one for the controller's registered office. Consumer disputes, ODR is available via ",
    s9LinkLabel: "ec.europa.eu/consumers/odr",
    s9After: ".",
  },
};

// --- German ------------------------------------------------------------

const de: LegalCopy = {
  privacy: {
    topBarBack: "Zurück",
    topBarTitle: "RICHTLINIE",
    h1: "Richtlinie",
    metaUpdated: "AKTUALISIERT · 14. MAI 2026",

    s1Heading: "1. Verantwortlicher",
    s1Body:
      "Verantwortlicher für deine personenbezogenen Daten in der Anwendung Sytofood ist der Projektinhaber. Kontakt: hello@sytofood.com.",

    s2Heading: "2. Welche Daten wir verarbeiten",
    s2ItemIdentifyingLead: "Identifizierend:",
    s2ItemIdentifyingRest:
      " E-Mail, Vorname (optional), Passwort-Hash (PBKDF2 mit 100.000 Iterationen, wir speichern das Passwort niemals im Klartext).",
    s2ItemHealthLead: "Gesundheitsprofil",
    s2ItemHealthRest:
      " (Art. 9 DSGVO (RODO), besondere Kategorie): Gewicht, Größe, Geburtsjahr, biologisches Geschlecht, Aktivitätsniveau, Kalorienziel und Makronährstoffe. Alle diese Felder sind optional.",
    s2ItemFoodLead: "Ernährungsdaten:",
    s2ItemFoodRest:
      " erfasste Mahlzeiten (Name, kcal, Makros, Datum), Favoriten, Kühlschrankinhalt, Einkaufsliste.",
    s2ItemOpsLead: "Betrieblich:",
    s2ItemOpsRest:
      " Protokolle (Aktionen wie Anmeldung, Erfassen einer Mahlzeit), IP-Adresse in den Rate-Limit-Schlüsseln (bei Anmeldung und Registrierung).",
    s2ItemSessionLead: "Sitzung:",
    s2ItemSessionBefore: " das Cookie ",
    s2ItemSessionAfter:
      " (HttpOnly, Secure, SameSite=Lax), ein funktionales Cookie, das für den Betrieb erforderlich ist.",

    s3Heading: "3. Rechtsgrundlagen",
    s3ItemContractLead: "Vertragserfüllung",
    s3ItemContractRest:
      " (Art. 6 Abs. 1 lit. b DSGVO (RODO)), für die zentrale Kalorienzählung und die Kontoverwaltung.",
    s3ItemConsentLead: "Ausdrückliche Einwilligung",
    s3ItemConsentRest:
      " (Art. 9 Abs. 2 lit. a DSGVO (RODO)), für Gesundheitsdaten (Gewicht, Größe, Geschlecht, kcal, Mahlzeitenfotos durch die KI). Du kannst sie jederzeit widerrufen, indem du dein Konto löschst.",
    s3ItemInterestLead: "Berechtigtes Interesse",
    s3ItemInterestRest:
      " (Art. 6 Abs. 1 lit. f DSGVO (RODO)), Sicherheit (IP-Rate-Limit) und Audit der Kontovorgänge.",

    s4Heading: "4. An wen wir Daten weitergeben",
    s4ItemCloudflareLead: "Cloudflare, Inc.",
    s4ItemCloudflareRest:
      " (USA, EU-Regionen): Hosting der Anwendung, D1-Datenbank, AI Gateway, Workers AI, Email Sending. DPA + SCC.",
    s4ItemAnthropicLead: "Anthropic, PBC",
    s4ItemAnthropicBefore:
      " (USA): Fotos von Speisekarten, Kühlschrank und Mahlzeiten werden über das AI Gateway an Claude Sonnet 4.6 gesendet, um deren Inhalt zu erkennen. Fotos ",
    s4ItemAnthropicEmph: "werden nicht gespeichert",
    s4ItemAnthropicAfter: ", weder bei uns noch bei Anthropic. DPA + SCC.",
    s4NoSellLead: "Wir verkaufen",
    s4NoSellRest:
      " deine Daten nicht. Wir verwenden keine Analyse-Tools von Dritten (Google, Meta usw.).",

    s5Heading: "5. Wie lange wir Daten speichern",
    s5ItemAccount:
      "Kontodaten und Mahlzeiten, solange du dein Konto behältst. Nach dem Löschen des Kontos werden sie sofort entfernt (Datenbank-Kaskade).",
    s5ItemAudit:
      "Audit der Vorgänge, 12 Monate, danach automatische Bereinigung.",
    s5ItemRateLimit:
      "Rate-Limit (IP), 7 Tage, danach automatische Bereinigung.",
    s5ItemPhotos:
      "Fotos (Speisekarte, Kühlschrank, Mahlzeit), niemals gespeichert. Versand an die KI, Antwort, Ende.",

    s6Heading: "6. Deine Rechte",
    s6ItemAccessLead: "Auskunft und Übertragbarkeit",
    s6ItemAccessRest:
      " (Art. 15, 20), lade im Dashboard eine JSON-Datei mit all deinen Daten herunter.",
    s6ItemRectifyLead: "Berichtigung",
    s6ItemRectifyRest:
      " (Art. 16), bearbeite Profil, Mahlzeiten und Kühlschrank in der Anwendung.",
    s6ItemEraseLead: "Löschung",
    s6ItemEraseRest:
      ' (Art. 17, "Recht auf Vergessenwerden"), die Schaltfläche "Konto löschen" im Dashboard. Endgültige Löschung, ohne Wiederherstellung.',
    s6ItemObjectLead: "Widerspruch",
    s6ItemObjectRest:
      " (Art. 21), wenn du der KI-Verarbeitung nicht zustimmst, nutze die Foto-Funktionen nicht. Ohne Fotos funktioniert die Anwendung weiterhin.",
    s6ItemComplaintLead: "Beschwerde",
    s6ItemComplaintRest:
      ", bei einer Aufsichtsbehörde (in Polen: der Präsident der UODO, uodo.gov.pl).",

    s7Heading: "7. Medizinischer Hinweis",
    s7Before: "Sytofood ",
    s7Emph: "ersetzt keine Beratung durch eine Ernährungsberatung oder einen Arzt",
    s7Middle:
      ". Kalorien- und Makronährstoffwerte sind KI-Schätzungen mit einer Fehlertoleranz. Bei einer Essstörung wende dich bitte an die Telefonseelsorge: ",
    s7Phone: "116 123",
    s7After: ".",

    s8Heading: "8. Cookies",
    s8Before: "Wir verwenden ein einziges funktionales Cookie (",
    s8After:
      ", HttpOnly). Es ist für die Anmeldung erforderlich, ohne es kannst du dein Konto nicht nutzen. Nach der ePrivacy-Richtlinie bedarf es keiner Einwilligung. Wir verwenden keine Tracking-Cookies.",

    s9Heading: "9. Kontakt",
    s9Body:
      "Fragen zum Datenschutz, Meldungen, Datenanfragen: hello@sytofood.com. Wir antworten innerhalb von 30 Tagen (Art. 12 Abs. 3 DSGVO (RODO)).",
  },
  terms: {
    topBarBack: "Zurück",
    topBarTitle: "AGB",
    h1: "AGB",
    metaUpdated: "AKTUALISIERT · 14. MAI 2026",

    s1Heading: "1. Was Sytofood ist",
    s1Body:
      "Sytofood ist eine Webanwendung (PWA) zum Kalorienzählen und zur Mahlzeitenplanung, mit einem virtuellen Kühlschrank und KI-Vorschlägen. Die Nutzung setzt das Erstellen eines Kontos voraus.",

    s2Heading: "2. Konto",
    s2ItemAge:
      "Mindestalter: 16 Jahre (oder mit Einwilligung der Erziehungsberechtigten).",
    s2ItemAccount:
      "Ein Konto pro Person. Passwort mit mindestens 8 Zeichen.",
    s2ItemHousehold:
      "Du kannst über einen Einladungscode einem Haushalt (Familie) beitreten, dann teilst du mit den anderen Mitgliedern die Produktliste, den Kühlschrank und die Einkäufe. Deine Mahlzeiten bleiben standardmäßig privat.",
    s2ItemDelete:
      "Du kannst dein Konto jederzeit löschen (Dashboard → Konto löschen).",

    s3Heading: "3. Was wir mit KI machen",
    s3Before:
      "Fotos von Mahlzeiten, Kühlschrank und Speisekarten werden über das Cloudflare AI Gateway an Claude Sonnet 4.6 (Anthropic, USA) gesendet. Fotos ",
    s3Emph1: "werden nicht gespeichert",
    s3Middle: ". Die kcal/Makro-Schätzungen sind ",
    s3Emph2: "KI-Schätzungen mit einer Fehlertoleranz",
    s3After:
      ", betrachte sie nicht als medizinische oder ernährungsbezogene Beratung.",

    s4Heading: "4. Medizinischer Hinweis",
    s4IntroEmph: "Sytofood ist kein Medizinprodukt.",
    s4IntroRest:
      " Die Anwendung stellt keine Diagnosen, empfiehlt keine Behandlung und ersetzt keinen Besuch bei einer Ernährungsberatung oder einem Arzt. Bei einer Essstörung wende dich an:",
    s4ItemAdultsLead: "Telefonseelsorge für Erwachsene: ",
    s4ItemAdultsPhone: "116 123",
    s4ItemChildrenLead: "Telefonseelsorge für Kinder und Jugendliche: ",
    s4ItemChildrenPhone: "116 111",
    s4ItemEmergencyLead: "Notrufnummer: ",
    s4ItemEmergencyPhone: "112",

    s5Heading: "5. Was nicht erlaubt ist",
    s5ItemReverse:
      "Reverse Engineering, Scrapen der API, Umgehen von Rate-Limits.",
    s5ItemUpload:
      "Hochladen fremder Fotos oder fremder Gesundheitsdaten.",
    s5ItemPromote:
      "Nutzung der Anwendung zur Förderung ungesunder Verhaltensweisen.",
    s5ItemSpam: "Spam oder automatisierte Konten.",

    s6Heading: "6. Preis und Verfügbarkeit",
    s6Body1:
      "Sytofood ist derzeit in der Testphase kostenlos. Wir können künftig einen kostenpflichtigen Plan für neue Funktionen einführen, bestehende Konten erhalten mindestens 30 Tage im Voraus eine Benachrichtigung.",
    s6Body2:
      "Best-Effort-Verfügbarkeit, kein SLA. Wir können ein Konto bei einem Verstoß gegen die AGB nach vorheriger Benachrichtigung sperren.",

    s7Heading: "7. Haftung",
    s7Body:
      "Du nutzt Sytofood auf eigene Verantwortung. Wir haften nicht für die Folgen von Ernährungsweisen, die auf Grundlage von KI-Schätzungen festgelegt wurden, konsultiere bei einer Eliminationsdiät, einer Schwangerschaft oder einer chronischen Erkrankung stets eine Ernährungsberatung.",

    s8Heading: "8. Änderungen der AGB",
    s8Body:
      "Wir benachrichtigen dich 14 Tage vor Inkrafttreten der Änderungen per E-Mail. Wenn du sie nicht akzeptierst, kannst du dein Konto mit einem vollständigen Datenexport löschen.",

    s9Heading: "9. Recht und Gerichtsstand",
    s9Before:
      "Polnisches Recht. Örtlich zuständig ist das Gericht am Sitz des Verantwortlichen. Bei Verbraucherstreitigkeiten ist eine OS-Beilegung verfügbar über ",
    s9LinkLabel: "ec.europa.eu/consumers/odr",
    s9After: ".",
  },
};

export const legalCopy: Record<Lang, LegalCopy> = { pl, en, de };
